import type { Vec3 } from '../core/math';
import { CRITICAL_LANDMARKS, LM } from './landmarks';

/**
 * Pose quality estimation.
 *
 * This lives in its own module — rather than as a private method on PoseSource —
 * because it is the single point of failure that decides whether the app can
 * start at all, and PoseSource cannot be instantiated without a camera.
 * Extracting it makes it reachable from the headless harness.
 *
 * Context: MediaPipe's **Tasks** PoseLandmarker does not populate the
 * `visibility` field. Every landmark comes back as exactly 0, unlike the legacy
 * Solutions API which the docs and most tutorials describe. A quality metric
 * built on `visibility` therefore returns 0 for every frame forever, on every
 * machine, which silently rejects 100% of calibration samples.
 */

/** True when the model is not actually reporting per-landmark visibility. */
export function visibilityIsUnavailable(vis: readonly number[]): boolean {
  let sum = 0;
  for (const v of vis) sum += v;
  return sum <= 1e-6;
}

export function visibilityQuality(vis: readonly number[], floor: number): number {
  let ok = 0;
  for (const idx of CRITICAL_LANDMARKS) if (vis[idx] >= floor) ok++;
  return ok / CRITICAL_LANDMARKS.length;
}

/**
 * "Does this look like a torso, seen well enough to measure?"
 *
 * Deliberately loose — a trust gate, not a pose classifier — but between them
 * these checks reject the failure modes that actually occur: nobody in frame,
 * only a head visible, a hallucinated folded skeleton, or the subject half out
 * of shot. It is also a *better* signal than per-point visibility ever was,
 * because it validates the exact quantities the game goes on to measure.
 */
export function geometricQuality(world: readonly Vec3[], image: readonly Vec3[]): number {
  for (const idx of CRITICAL_LANDMARKS) {
    const p = world[idx];
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) return 0;
  }

  const ls = world[LM.LEFT_SHOULDER];
  const rs = world[LM.RIGHT_SHOULDER];
  const lh = world[LM.LEFT_HIP];
  const rh = world[LM.RIGHT_HIP];

  const shoulderW = Math.hypot(ls.x - rs.x, ls.y - rs.y, ls.z - rs.z);
  const hipW = Math.hypot(lh.x - rh.x, lh.y - rh.y, lh.z - rh.z);
  const midShoulderY = (ls.y + rs.y) * 0.5;
  const midHipY = (lh.y + rh.y) * 0.5;
  const torso = Math.abs(midHipY - midShoulderY);

  let score = 0;
  // Shoulders a human width apart, in metres.
  if (shoulderW > 0.18 && shoulderW < 0.75) score += 0.25;
  // Hips present and not wider than a person's shoulders by much.
  if (hipW > 0.08 && hipW < shoulderW * 1.4) score += 0.15;
  // Torso length sane relative to shoulder width.
  if (torso > shoulderW * 0.6 && torso < shoulderW * 2.6) score += 0.25;
  // Head above the hips. World +y is DOWN in MediaPipe's convention.
  if (world[LM.NOSE].y < midHipY - 0.15) score += 0.15;
  // The body we measured is genuinely inside the frame, not extrapolated off it.
  const framed = [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER, LM.LEFT_HIP, LM.RIGHT_HIP, LM.NOSE];
  let inside = 0;
  for (const idx of framed) {
    const p = image[idx];
    if (p && p.x > 0.02 && p.x < 0.98 && p.y > 0.02 && p.y < 0.98) inside++;
  }
  score += 0.2 * (inside / framed.length);

  return score;
}

/** The metric PoseSource actually publishes. */
export function poseQuality(
  world: readonly Vec3[],
  image: readonly Vec3[],
  vis: readonly number[],
  floor: number,
  visUnavailable: boolean,
): number {
  const geo = geometricQuality(world, image);
  if (visUnavailable) return geo;
  // When visibility IS reported, blend: an implausible-but-"visible" pose must
  // still score low, so geometry can veto but cannot single-handedly pass.
  return Math.min(visibilityQuality(vis, floor), geo * 0.4 + 0.6);
}
